/** @jsxImportSource @emotion/react */
import { useDroppable } from '@dnd-kit/core';
import { css } from '@emotion/react';
import type { ReactNode } from 'react';

import { ExpandCaret } from '@/Client/Icons';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import {
  KEBAB_GUTTER_HOVER_STYLE,
  KEBAB_GUTTER_OPEN_STYLE,
  KEBAB_GUTTER_TRANSITION_STYLE,
  KEBAB_HOST_ROW_PROPS,
  KEBAB_MENU_OPEN_RAISE_STYLE,
} from '@/Client/Widgets';
import { pluralize } from '@art-suite/art-core-ts';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { MetaList } from '../../Widgets/MetaList/MetaList';
import type { VideoToolboxClipReadStatus } from '../VideoToolbox/videoToolboxClipReadStatusLib';
import { KaiNavGroupVideoUnreadChip } from './KaiNavGroupVideoUnreadChip';
import { DEFAULT_GROUP_COST } from './KaiNavigator';
import { KaiNavRowMenu } from './KaiNavRowMenu';
import {
  amountStyle,
  MUTED_TEXT_STYLE,
  selectableRowStyle,
  TRAILING_STACK_STYLE,
} from './navigatorStyles';
import { selectRowOnEnterOrSpace } from './rowSelection';

/* ── group header (mockup: grp-hdr) ──
   One flex row: caret, the name with its task count beneath it, then a
   trailing cluster pushed right by margin-left:auto. The cluster holds the
   section's total stacked above its approve control, same as a task row, then
   a kebab for duplicating or deleting the section itself. The name and task
   count share a shrinkable column, so both truncate instead of sitting under
   a widened trailing cluster.

   Two controls share the row — the caret and the row itself — each firing its
   own callback (`onToggleExpanded`/`onSelect`), so a host that genuinely wants
   them to do different things still can. KaiNavigator itself wires both to the
   very same function: selecting a group and expanding it are not two actions
   worth telling apart here, and a caret that could open or close a group
   independent of selection is what a forced auto-scroll effect used to have
   to chase — see KaiNavigator's handleSelectGroup for why that effect is gone
   entirely now instead.

   The row itself is the select target (not a nested button) so the design's
   padding and gap apply to one box; keyboard support is added explicitly. */

const SCOPE_GROUP_HEADER_STYLE = css`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 16px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
  position: sticky;
  top: 0;
  z-index: 2;

  &:focus-visible { outline: 2px solid ${COLORS.ink}; outline-offset: -2px; }
`;

/** A dragged photo is hovering this header — same green convention as the old
 *  ScanEditor's InlineTaskEditorTaskBar drop target. */
const SCOPE_GROUP_HEADER_DROP_OVER_STYLE = css`
  background: var(--textGreen2);
`;

const SCOPE_GROUP_CARET_BUTTON_STYLE = css`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 3px;
  background: transparent;
  color: ${COLORS.textTertiary};
  cursor: pointer;
  transition: background 120ms ease, color 120ms ease;

  &:hover { color: ${COLORS.ink}; background: rgba(0, 0, 0, 0.06); }
`;

const SCOPE_GROUP_TITLE_STACK_STYLE = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

const SCOPE_GROUP_NAME_STYLE = css`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: ${FONT.display};
  font-size: ${pxToRem(13)};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
  color: ${COLORS.ink};
`;

/** Lifts a zero-cost group's header off the surrounding sunken background,
 *  reinforcing "nothing here yet" alongside its muted text. */
const SCOPE_GROUP_HEADER_ZERO_COST_STYLE = css`
  background: ${COLORS.paper};
`;

/* Money reads as data, not display type: body face with tabular figures so
   amounts line up down the sidebar, and always ink — colour is carried by the
   status pill, not the number. */
const GROUP_TOTAL_FONT_SIZE = 16;

/* Everything right of the name rides in one cluster pushed to the right edge.
   The cluster carries the auto margin rather than any one of its contents, so
   a control that renders nothing — an approve chip out of season — neither
   takes up room nor takes the push with it. */
const SCOPE_GROUP_HEADER_TRAILING_STYLE = css`
  flex-shrink: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 9px;
`;

/**
 * The header of a collapsible section in the navigator.
 *
 * "Group" is the general sense here, not the scan sense: the Contractor tab
 * buckets the same tasks a different way, and its buckets are sections too.
 * So this takes an id and a title rather than a `ScanGroupOptimized`, and any
 * per-section controls arrive through `actions`.
 */
export const KaiNavTaskGroupHeader = ({
  id,
  dropGroupId,
  title,
  taskCount,
  photoCount,
  videoCount,
  videoClipReadStatus,
  costLabel,
  expanded,
  selected,
  actions,
  onSelect,
  onToggleExpanded,
  onDelete,
  onDuplicate,
  approvalOffered = false,
}: {
  id: string;
  /**
   * This header's real group id, when it represents one — omitted for a
   * Contractor-tab bucket header, which isn't a scope part and so can't be a
   * photo-drop target the way a real group header can.
   */
  dropGroupId?: string;
  title: string;
  taskCount: number;
  photoCount: number;
  videoCount: number;
  /** Group headers only — when NOT READ, replaces the video count with a grp-chip. */
  videoClipReadStatus?: VideoToolboxClipReadStatus;
  costLabel: string | undefined;
  expanded: boolean;
  selected: boolean;
  /** The section's own approve chip, stacked above the total. */
  actions?: ReactNode;
  onSelect: (id: string) => void;
  onToggleExpanded: (id: string) => void;
  /** Kebab actions, absent when the scope is locked or the host offers none —
   *  same convention as a task row's own `onDelete`/`onDuplicate`. */
  onDelete?: () => void;
  onDuplicate?: () => void;
  /** Whether `actions` is actually an approve control right now — reserves the same
   *  gutter a task row does, and keeps the kebab (if this header has one) visible
   *  outright rather than hover-only. */
  approvalOffered?: boolean;
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `navigator-group-${id}`,
    data: { type: 'group', groupId: dropGroupId },
    disabled: !dropGroupId,
  });
  const hasRowMenu = !!onDelete || !!onDuplicate;
  const costIsZero = costLabel === DEFAULT_GROUP_COST;
  const showUnreadVideoChip = !!dropGroupId
    && videoClipReadStatus === 'NOT READ'
    && videoCount > 0;
  return (
    <div
      ref={setNodeRef}
      css={[
        SCOPE_GROUP_HEADER_STYLE,
        KEBAB_MENU_OPEN_RAISE_STYLE,
        costIsZero && SCOPE_GROUP_HEADER_ZERO_COST_STYLE,
        selectableRowStyle({ selected }),
        isOver && SCOPE_GROUP_HEADER_DROP_OVER_STYLE,
      ]}
      role='button'
      tabIndex={0}
      aria-label={`Select ${title}`}
      aria-pressed={selected}
      data-closeout-scope-group-header='true'
      {...KEBAB_HOST_ROW_PROPS}
      data-selected={selected ? 'true' : 'false'}
      onClick={() => onSelect(id)}
      onKeyDown={selectRowOnEnterOrSpace(() => onSelect(id))}
    >
      <button
        type='button'
        css={SCOPE_GROUP_CARET_BUTTON_STYLE}
        data-testid='kai-navigator-group-caret'
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${title}`}
        onClick={event => {
          event.stopPropagation();
          onToggleExpanded(id);
        }}
      >
        <ExpandCaret expanded={expanded} initialRotation={90} finalRotation={180} />
      </button>
      <div css={SCOPE_GROUP_TITLE_STACK_STYLE}>
        <span css={[SCOPE_GROUP_NAME_STYLE, costIsZero && MUTED_TEXT_STYLE]}>{title}</span>
        <MetaList
          parts={[
            ...taskCount ? [pluralize(taskCount, 'task')] : [],
            ...photoCount ? [pluralize(photoCount, 'photo')] : [],
            ...showUnreadVideoChip
              ? [<KaiNavGroupVideoUnreadChip key={'video-unread'} count={videoCount} />]
              : videoCount
              ? [pluralize(videoCount, 'video')]
              : [],
          ]}
        />
      </div>
      <div
        css={[
          SCOPE_GROUP_HEADER_TRAILING_STYLE,
          KEBAB_GUTTER_TRANSITION_STYLE,
          hasRowMenu && KEBAB_GUTTER_HOVER_STYLE,
          approvalOffered && KEBAB_GUTTER_OPEN_STYLE,
        ]}
      >
        <div css={TRAILING_STACK_STYLE}>
          <span css={[amountStyle(GROUP_TOTAL_FONT_SIZE), costIsZero && MUTED_TEXT_STYLE]}>{costLabel}</span>
          {actions}
        </div>
      </div>
      {
        /* A sibling of the trailing cluster, not a child of it: `transform` on that
        cluster makes it a containing block for absolutely-positioned descendants, so a
        kebab nested inside it would slide with it instead of staying pinned to the
        header's own right edge. Flex placement is moot either way — an
        absolutely-positioned flex item is out of flow. */
      }
      <KaiNavRowMenu onDelete={onDelete} onDuplicate={onDuplicate} label={title} alwaysVisible={approvalOffered} />
    </div>
  );
};
